import { KeyRound, Loader2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ApiError, adminApi, studentApi } from "@/lib/api";
import { adminKeyStore, apiKeyStore } from "@/lib/auth";

type Mode = "student" | "admin";

const COPY: Record<Mode, { title: string; description: string; label: string }> = {
  student: {
    title: "Sign in",
    description: "Stored in this browser only.",
    label: "API key",
  },
  admin: {
    title: "Admin sign in",
    description: "Stored in this browser only.",
    label: "Admin key",
  },
};

/**
 * Credential entry, validating the key before storing it.
 *
 * Validating first matters: an unchecked key gets saved and then every
 * subsequent page shows an authentication error, which reads as "the site is
 * broken" rather than "you mistyped your key".
 */
export function ApiKeyDialog({
  mode = "student",
  trigger,
  open,
  onOpenChange,
}: {
  mode?: Mode;
  trigger?: React.ReactElement;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}) {
  const [value, setValue] = useState("");
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const copy = COPY[mode];

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    const key = value.trim();
    if (!key) return;

    setChecking(true);
    setError(null);
    try {
      if (mode === "admin") {
        await adminApi.keys(key);
        adminKeyStore.set(key);
      } else {
        const me = await studentApi.me(key);
        apiKeyStore.set(key);
        toast.success(`Signed in as ${me.email}`);
      }
      setValue("");
      onOpenChange?.(false);
    } catch (caught) {
      let message = "Could not verify the key.";
      if (caught instanceof ApiError) {
        // 503 means the server has no ADMIN_KEY set, so no key would work here.
        // Saying "key not recognised" would send the TA hunting for the wrong bug.
        message = caught.isNotConfigured
          ? caught.message
          : caught.isUnauthorized
            ? "Key not recognised."
            : caught.message;
      }
      setError(message);
    } finally {
      setChecking(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {trigger ? <DialogTrigger render={trigger} /> : null}
      <DialogContent className="sm:max-w-md">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <KeyRound className="size-4" aria-hidden />
              {copy.title}
            </DialogTitle>
            <DialogDescription>{copy.description}</DialogDescription>
          </DialogHeader>

          <div className="grid gap-2 py-4">
            <Label htmlFor="api-key">{copy.label}</Label>
            <Input
              id="api-key"
              type="password"
              autoComplete="off"
              spellCheck={false}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder="paste your key"
              aria-invalid={error ? true : undefined}
              aria-describedby={error ? "api-key-error" : undefined}
            />
            {error ? (
              <p id="api-key-error" className="text-destructive text-sm">
                {error}
              </p>
            ) : null}
          </div>

          <DialogFooter>
            <Button type="submit" disabled={checking || !value.trim()}>
              {checking ? (
                <>
                  <Loader2 className="size-4 animate-spin" aria-hidden />
                  Verifying
                </>
              ) : (
                "Sign in"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
